const APPLE_PAY_SUPPORTED_VERSIONS = [14, 13];
/**
 * @implements {IAltaPayMethodHandler}
 */
export class AltaPayApplePay {

    applePaySession;
    token;
    sessionId;
    paymentMethodId;
    order;
    paymentId;
    merchantIdentifier;
    onInitiatePaymentUrl;
    operatingCountry;
    renderChecked;
    supportedNetworks;
    apiVersion;
    applePayLoaded;

    constructor(
        token,
        sessionId,
        paymentMethodId,
        order,
        data) {
        this.token = token;
        this.sessionId = sessionId;
        this.paymentMethodId = paymentMethodId;
        this.order = order;
        this.merchantIdentifier = data.merchantIdentifier;
        this.operatingCountry = data.operatingCountry;
        this.supportedNetworks = data.supportedNetworks;
        this.onInitiatePaymentUrl = data.onInitiatePaymentUrl;
        this.renderChecked = false;
        this.applePayLoaded = this.applePayLoad();
    }

    /**
     *
     * @returns {Promise}
     */
    applePayLoad() {
        return new Promise(resolve => {
            const element = document.createElement("script");
            element.addEventListener("load", resolve, { once : true });
            element.src = "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js"
            element.crossOrigin = "anonymous";
            document.head.appendChild(element);
        })
    }

    /**
     *
     * @returns {Promise}
     */
    onRenderCheck() {
        const error = (message) => Promise.reject({
            message,
            errorCode: 313008
        });

        const that = this;
        return that.applePayLoaded.then(() => {

            if (!window.ApplePaySession) {
                return error("Apple Pay is not available on the device.");
            }
            that.selectSupportedVersion(that);
            if (!that.apiVersion) {
                return error("Required version of Apple Pay is not supported. Minimal required version: "
                    + APPLE_PAY_SUPPORTED_VERSIONS[APPLE_PAY_SUPPORTED_VERSIONS.length - 1]);
            }

            const onAvailable = () => {
                console.debug("ApplePay is available for merchant:", that.merchantIdentifier);
                that.renderChecked = true;
                return Promise.resolve();
            };
            const onUnavailable = () => {
                console.debug("ApplePay is unavailable for merchant:", that.merchantIdentifier);
                return error("Apple Pay is not available at the moment or not configured on the device.");
            };

            if (ApplePaySession.applePayCapabilities) {
                return ApplePaySession.applePayCapabilities(that.merchantIdentifier)
                    .then(capabilities => capabilities.paymentCredentialStatus === "paymentCredentialsAvailable" ? onAvailable() : onUnavailable());
            }
            return Promise.resolve(ApplePaySession.canMakePayments())
                .then(canMakePayments => canMakePayments ? onAvailable() : onUnavailable());
        });
    }

    selectSupportedVersion(that) {
        for (const version of APPLE_PAY_SUPPORTED_VERSIONS) {
            if (ApplePaySession.supportsVersion(version)) {
                that.apiVersion = version;
                return;
            }
        }
    }

    onInitiatePayment() {
        if (!this.renderChecked) {
            return Promise.reject({
                message: "Apple Pay is not available on the device.",
                errorCode: 313008
            });
        }
        const that = this;

        const paymentRequest = that.createApplePayPaymentRequest(that.operatingCountry, that.order);
        const session = new ApplePaySession(that.apiVersion, paymentRequest);
        that.applePaySession = session;
        session.onvalidatemerchant = that.onvalidatemerchant.bind(this);
        session.onpaymentmethodselected = that.onpaymentmethodselected.bind(this);
        session.onpaymentauthorized = that.onpaymentauthorized.bind(this);
        session.oncancel = that.oncancel.bind(this);
        session.begin();

        return Promise.resolve();
    }

    onvalidatemerchant(validateMerchantEvent) {
        console.debug("onvalidatemerchant executed:", validateMerchantEvent);

        const that = this;
        let paymentRequest = {
            "sessionId": that.sessionId,
            "paymentMethodId": that.paymentMethodId,
            "requestData": {
                "applePayRequestData": {
                    "source": "APPLE_PAY_JS_API",
                    "validationUrl": validateMerchantEvent.validationURL,
                }
            },
        };

        fetch(that.onInitiatePaymentUrl, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + that.token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentRequest)
        }).then(response => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error("couldn't validate merchant session");
            }
        }).then(response => {
            that.paymentId = response.paymentId;
            if (response.data) {
                const merchantSession = JSON.parse(response.data);
                console.debug(merchantSession);
                that.applePaySession.completeMerchantValidation(merchantSession);
            } else {
                throw new Error("no merchant session returned");
            }
            console.debug("Success onvalidatemerchant");
        }).catch(error => {
            console.error('Error onvalidatemerchant:', error);
        });
    }


    onpaymentmethodselected() {
        const that = this;
        that.applePaySession.completePaymentMethodSelection(
            {
                newTotal: {
                    label: that.order.orderId,
                    type: "final",
                    amount: that.order.amount.value
                },
                newLineItems: []
            }
        );
    }

    oncancel(payload) {
        console.debug("oncancel executed: ", payload);
    }

    onpaymentauthorized(paymentAuthorizedEvent) {
        console.debug("onpaymentauthorised executed:", paymentAuthorizedEvent);
        const that = this;

        let payment = paymentAuthorizedEvent.payment;
        let authorizeRequest = {
            "sessionId": that.sessionId,
            "paymentMethodId": that.paymentMethodId,
            "paymentId": that.paymentId,
            "requestData": payment.token
        };
        fetch(that.onInitiatePaymentUrl + '/' + that.paymentId + '/authorization', {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + that.token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(authorizeRequest)
        }).then(response => {
            console.debug("Response onpaymentauthorised:", response)
            if (response.status !== 200) {
                throw 'Received status: ' + response.status;
            }
            return response.json();
        }).then(data => {
                that.paymentId = data.paymentId;
                let result = {};
                result.status = ApplePaySession.STATUS_FAILURE;
                result.errors = [];
                if (data.status === 'succeeded') {
                    result.status = ApplePaySession.STATUS_SUCCESS;
                }
                that.applePaySession.completePayment(result);
                if (data.resultUrl) {
                    window.AltaPayUtils.redirect(data.resultUrl);
                } else if(data.resultContent){
                    that.createIFrameWithCallbackContent(data.resultContent);
                }
            }
        ).catch((error) => {
            console.error('Error onpaymentauthorised:', error);
        });
    }

    /**
     *
     * @param htmlContent
     */
    createIFrameWithCallbackContent(htmlContent){
        const background = document.createElement("div");
        background.className = 'AltaPayEmbeddedApplePayResultsBackground';
        background.style.backgroundColor = 'white';
        background.style.position = 'fixed';
        background.style.top = '0px';
        background.style.left = '0px';
        background.style.width = '100%';
        background.style.height = '100%';

        const frame = document.createElement("iframe");
        frame.className = 'AltaPayEmbeddedApplePayResults';
        frame.setAttribute('frameBorder', '0');
        frame.setAttribute('scrolling', 'auto');
        frame.setAttribute('style','position:fixed;top:0px;left:0px;width:100%;height:100%;');
        frame.srcdoc=htmlContent;

        document.body.appendChild(background);
        background.appendChild(frame);
    }

    /**
     *
     * @param {String} operatingCountry
     * @param {Order} order
     * @returns {{total: {label}, countryCode: string, supportedNetworks, merchantCapabilities, currencyCode: string}}
     */
    createApplePayPaymentRequest(operatingCountry, order) {
        let billingAddress = null;
        let shippingAddress = null;
        if(order.customer){
            billingAddress = order.customer.billingAddress;
            shippingAddress = order.customer.shippingAddress;
        }

        return {
            countryCode: operatingCountry,
            currencyCode: order.amount.currency,
            supportedNetworks: this.supportedNetworks,
            merchantCapabilities: ["supports3DS"],
            total: {
                label: order.orderId,
                type: "final",
                amount: order.amount.value
            },
            lineItems: this.getOrderLines(order),
            billingContact: this.getAddress(order.customer, billingAddress),
            shippingContact: this.getAddress(order.customer, shippingAddress)
        };
    }

    /**
     *
     * @param {Order} order
     * @returns {*[]}
     */
    getOrderLines(order) {
        let orderLines = [];
        if (order && order.orderLines) {
            order.orderLines.forEach(ol => {

                orderLines.push({
                    label: ol.description,
                    type: "final",
                    amount: ol.unitPrice //this is currently done like that, and it is wrong!!!
                })
            });
        }
        return orderLines;
    }

    getAddress(customer, address) {
        if (customer == null) {
            return null;
        }
        if (address == null) {
            return {
                givenName: customer.firstName,
                familyName: customer.lastName,
                addressLines: [],
                locality: null,
                country: null,
                postalCode: null
            };
        }
        return {
            givenName: customer.firstName,
            familyName: customer.lastName,
            addressLines: [
                address.street
            ],
            locality: address.city,
            country: address.country,
            postalCode: address.zipCode
        };
    }
}

